import * as preact from "preact";

import * as analytics from "analytics";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import type { TTSFeature, VoiceOption } from "tts";
import * as tts from "tts";
import Dialog from "views/Dialog";
import { TTSRateSelector, TTSVoiceRow } from "views/TTSVoiceControls";

interface Props {
  language: string;
  feature: TTSFeature;
  close: () => void;
}

export class TTSUpsellModal extends preact.Component<Props> {
  componentDidMount() {
    analytics.trackEventWithProperties("ttsUpsellShown", {
      language: this.props.language,
      feature: this.props.feature,
      tier: stores.user.state.peek().subscriptionPlan,
    });
  }

  /** Preview a specific Readlang voice — server builds the text from `kind`. */
  previewVoice = (voice: VoiceOption) => {
    tts.previewVoice({
      user: stores.user,
      feature: this.props.feature,
      language: this.props.language,
      voiceId: voice.id,
      kind: "neutralSample",
    });
  };

  onPickRate = async (rate: number) => {
    await tts.setRate({
      user: stores.user,
      feature: this.props.feature,
      language: this.props.language,
      rate,
    });
    this.forceUpdate();
    const first = this.highQualityVoices()[0];
    if (first) this.previewVoice(first);
  };

  highQualityVoices = (): VoiceOption[] =>
    tts
      .getVoicePickerState({
        user: stores.user,
        feature: this.props.feature,
        language: this.props.language,
      })
      .groups.find((group) => group.id === "highQuality")?.voices ?? [];

  render() {
    const { language } = this.props;
    const isFree = !stores.user.hasPremiumAccess.value;
    const hdVoices = this.highQualityVoices();
    const languageName = languageMap.map[language]?.english ?? language;
    const rate = tts.getRate({
      user: stores.user,
      feature: this.props.feature,
      language,
    });
    const heading = isFree
      ? "Unlock high quality voices"
      : "High quality Read Aloud voices";

    return (
      <Dialog class="ttsUpsell" heading={heading}>
        <p class="ttsUpsellLead">
          {isFree
            ? `Natural ${languageName} voices generated by Readlang, so they sound consistent across your devices.`
            : `Natural ${languageName} Read Aloud narration generated by Readlang, so it sounds consistent across your devices.`}
        </p>

        <ul class="ttsUpsellVoiceList">
          {hdVoices.map((voice) => (
            <TTSVoiceRow
              voice={voice}
              variant="upsell"
              onPreview={() => this.previewVoice(voice)}
            />
          ))}
        </ul>

        <TTSRateSelector rate={rate} onPickRate={this.onPickRate} />

        {isFree ? (
          <div class="ttsUpsellTiers">
            <div class="ttsUpsellTier">
              <h3 class="ttsUpsellTierTitle">Premium</h3>
              <p class="ttsUpsellTierDescription">
                <strong>High quality voices</strong> for speaking words and
                phrases.
              </p>
              <a class="ttsUpsellCTA prominent" href="/account">
                Upgrade to Premium
              </a>
            </div>
            <div class="ttsUpsellTier">
              <h3 class="ttsUpsellTierTitle">Premium Plus</h3>
              <p class="ttsUpsellTierDescription">
                <strong>High quality voices</strong> for speaking whole texts.
              </p>
              <a class="ttsUpsellCTA prominent" href="/account">
                Upgrade to Premium Plus
              </a>
            </div>
          </div>
        ) : (
          <a class="ttsUpsellCTA prominent" href="/account">
            Upgrade to Premium Plus
          </a>
        )}
      </Dialog>
    );
  }
}

export default TTSUpsellModal;
